<template>
  <LineChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    class="make-a-copy"
    :card-options="cardOptions"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import LineChart from '@/components/cards/models/LineChart';
import { mapActions, mapGetters } from 'vuex';
import EventBus from '@/utils/event-bus';
import { OPERATION } from '@/services/charts-service';
import cardsModel from '@/mixins/cards-model';
import { sortSeries } from '@/utils/sorting';
import { DATA_TYPE } from '@/store/modules/cards/constants';
import { Flags } from '@/plugins/rollout';
import {
  removeSeparator,
  splitStringOnSeparator,
} from '@/utils/formatter';

export default {
  name: 'BaseTimeSeriesSacListening',
  components: {
    LineChart,
  },
  mixins: [
    cardsModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    operation: {
      type: String,
      default: OPERATION.COUNT,
    },
    field: {
      type: String,
      default: null,
    },
    group: {
      type: String,
      default: null,
    },
    filterName: {
      type: String,
      default: null,
    },

  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: [],
      pointsParser: 'series',
      filteredValues: [],
      categories: [],
      cardOptions: {
        chart: {
          type: 'areaspline',
        },
        legend: {
          enabled: true,
        },
        plotOptions: {
          areaspline: {
            fillOpacity: 0.05,
            marker: {
              enabled: false,
            },
          },
        },
        xAxis: {},
      },
      isPublicDashboard: false,
    };
  },
  computed: {
    undefinedLabeL() {
      return this.$t('DataManagement.undefined');
    },
    ...mapGetters('featureFlag', [
      'getValue',
    ]),
    series() {
      if (!this.rawSeries && !this.rawSeries.length) {
        return [];
      }

      let series = this.rawSeries;

      if (this.filteredValues.length) {
        series = series?.filter(point => {
          return this.filteredValues.includes(point.name);
        }) || [];
      }

      const organizedSeries = series?.map(serie => {
        const serieName = this.isPublicDashboard ? removeSeparator(serie.name) : serie.name;
        return {
          name: serieName || this.undefinedLabeL,
          data: serie.data.map((val, index) => {
            return [this.categories[index], val || 0];
          }),
        };
      });

      const sortedSeries = sortSeries(organizedSeries);
      return sortedSeries;
    },
  },
  async created() {
    this.getData();
    const publicDashboardInfo = await this.getValue(Flags.publicDashboardInfo);
    const token = splitStringOnSeparator(publicDashboardInfo, '|')[0] || '';

    this.isPublicDashboard = await Boolean(token);
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    orderDataArrayAsc(data) {
      return data.sort();
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      const promisses = [
        this.createRequisitions(DATA_TYPE.LISTENING, options),
        this.createRequisitions(DATA_TYPE.SAC, options),
      ];

      Promise.all(
        promisses
      ).then(([responseListening, responseSac]) => {
        this.requestStatus = 'success';
        const response = this.aggregateResponses(responseListening, responseSac);
        const axes = response.axes;
        const points = response.points;
        if (options?.silentRefresh) {
          EventBus.$emit('updated', { itemId: this.itemId });
        }

        if (!points) {
          return;
        }

        if (this.$attrs.filter) {
          this.filteredValues = this.$attrs.filter.listeningFilter?.[this.filterName]?.value || [];
        }

        const categories = (axes || [])[0]?.values;

        this.categories = this.formattedCategories(categories);

        this.rawSeries = points;
      })
        .catch(() => {
          this.requestStatus = 'error';
        });
    },
    formattedCategories(categories) {
      const rawCategories = this.orderDataArrayAsc([...categories]);

      return this.formattedCategoriesForQuarter(rawCategories);
    },
    createRequisitions(dataType, options) {
      return this.getChartsService({
        itemId: this.itemId,
        method: 'getTimeSeries',
        filterType: dataType,
        params: {
          ...(this.field ? { fields: [this.field] } : null),
          ...(this.group ? { grouping: [this.group] } : null),
          operators: this.operation,
          datePortion: this.getDatePortion(options),
        },
      });
    },
    aggregateResponses(res1, res2) {
      const pointsMap = new Map();

      const addPoints = (points) => {
        points.forEach(({ name, data }) => {
          if (!pointsMap.has(name)) {
            pointsMap.set(name, Array(data.length).fill(0));
          }
          pointsMap.set(name, pointsMap.get(name).map((val, idx) => val + data[idx]));
        });
      };

      addPoints(res1.points);
      addPoints(res2.points);

      const aggregatedPoints = Array.from(pointsMap, ([name, data]) => ({ name, data }));

      return {
        points: aggregatedPoints,
        axes: res1.axes,
      };
    },
  },
};
</script>

